// src/pages/User/RoadsideInspectionForm.js
import React, { useEffect, useState } from "react";
import { Container, Card } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import "../../styles/RoadsideInspection.css";
import CompanyAddress from "../../components/CompanyAddress";
import UploadedAttachmentList from "../../components/UploadedAttachmentList";
import logo from "../../images/logo.png";

const hazmatInspectionLevelOptions = ["Bulk", "Non-Bulk", "None"];
const inspectionLevelOptions = [
  "1 - North American Standard Inspection",
  "2 - Walk-around driver/vehicle Inspection",
  "3 - Driver/Credential/Administrative Inspection",
  "4 - Special Inspections",
  "5 - Vehicle-Only Inspection",
  "6 - North American Standard Inspection for Transuranic Waste and Highway Route Controlled Quantities (HRCQ) of Radioactive Material",
  "7 - Jurisdictional Mandated Commercial Vehicle Inspection",
  "8 - North American Standard Electronic Inspection",
];

const RoadsideInspectionForm = () => {
  const navigate = useNavigate();
  const { id: paramId, riId } = useParams();
  const isNew = !riId || riId === "new";
  const role = localStorage.getItem("userRole");
  const isAdmin = role === "1";
  const loggedInId = localStorage.getItem("userId");
  const userId = isAdmin && paramId ? paramId : loggedInId;

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // ---------- helpers
  const safeParse = (val, fb = []) => {
    try {
      if (Array.isArray(val) || typeof val === "object") return val || fb;
      return JSON.parse(val || "[]");
    } catch {
      return fb;
    }
  };

  // ---------- state
  const [form, setForm] = useState({
    ri_number: "",
    ri_dot_flag: true,
    ri_is_clean: false,
    ri_is_commercial: true,

    ri_inspection_level: "",
    ri_hazmat_level: "",

    ri_inspection_date: "",
    ri_start_time: "",
    ri_end_time: "",
    ri_reported_date: "",
    ri_reported_time: "",

    ri_location: "",
    ri_highway: "",
    ri_county: "",
    ri_shipper: "",
    ri_milepost: "",
    ri_state_province: "",
    ri_cargo: "",
    ri_origin: "",

    // JSON sections
    ri_vehicle_identification: [
      // default one row like image
      {
        unit: 1,
        number: "",
        type: "",
        license: "",
        vin: "",
        cvsa: "",
        oos: "",
      },
    ],
    ri_brake_adjustments: [
      { label: "Right", values: { 1: "", 2: "", 3: "", 4: "", 5: "" } },
      { label: "Left", values: { 1: "", 2: "", 3: "", 4: "", 5: "" } },
      { label: "Chamber", values: { 1: "", 2: "", 3: "", 4: "", 5: "" } },
    ],
    ri_violations: [
      // one empty row
      {
        sectionCode: "",
        unit: "",
        oos: false,
        citationNumber: "",
        verify: false,
        description: "",
      },
    ],
    ri_penalties: {
      flags: {
        suspension: false,
        revocation: false,
        communityService: false,
        other: false,
      },
      otherText: "",
      citations: [{ citationNumber: "", fine: 0, chargeBack: 0 }],
      includeAnnualReview: false,
      includeSeparationReport: false,
    },
    ri_tasks: [], // { taskType, comments, assignTo, group, dueDate, completed }

    // attachments (optional)
    attachments: [],
  });

  const [selectedAttachmentType, setSelectedAttachmentType] = useState("");
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);

  // ---------- effects
  useEffect(() => {
    if (isNew) return;
    axios
      .get(`${API_BASE}/api/roadside/detail/${riId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(({ data }) => {
        setForm((prev) => ({
          ...prev,
          ...data,
          ri_inspection_date: data.ri_inspection_date
            ? new Date(data.ri_inspection_date).toISOString().slice(0, 10)
            : "",
          ri_reported_date: data.ri_reported_date
            ? new Date(data.ri_reported_date).toISOString().slice(0, 10)
            : "",
          ri_vehicle_identification: safeParse(
            data.ri_vehicle_identification,
            []
          ),
          ri_brake_adjustments: safeParse(data.ri_brake_adjustments, []),
          ri_violations: safeParse(data.ri_violations, []),
          ri_penalties:
            typeof data.ri_penalties === "string"
              ? JSON.parse(data.ri_penalties || "{}")
              : data.ri_penalties || {},
          // ✅ ensure location always exists, even if missing from backend
          location: data.location || {
            location: "",
            milepost: "",
            highway: "",
            state: "",
            county: "",
            cargo: "",
            shipper: "",
            origin: "",
          },
        }));
      })

      .catch((err) => console.error("❌ Load roadside inspection", err));
  }, [isNew, riId, API_BASE]);

  useEffect(() => {
    if (isNew) return;
    axios
      .get(`${API_BASE}/api/roadside/attachments/${riId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => {
        const normalized = (res.data || []).map((a) => ({
          id: a.ria_id,
          file: null,
          type: a.ria_type,
          ria_file: a.ria_file,
          ria_original: a.ria_original,
        }));
        setForm((p) => ({ ...p, attachments: normalized }));
      })
      .catch((err) => console.error("❌ Load attachments", err));
  }, [isNew, riId, API_BASE]);

  // ---------- handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
  };

  const save = async () => {
    const headers = {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    };

    const payload = {
      ...form,
      ri_userid: userId,
      // keep arrays/objects as they are; backend stores into JSON column
      ri_vehicle_identification: form.ri_vehicle_identification || [],
      ri_brake_adjustments: form.ri_brake_adjustments || [],
      ri_violations: form.ri_violations || [],
      ri_penalties: form.ri_penalties || {},
      ri_tasks: form.ri_tasks || [],
    };

    try {
      let currentId = riId;
      if (isNew) {
        const res = await axios.post(`${API_BASE}/api/roadside`, payload, {
          headers,
        });
        currentId = res.data.id;
        alert("✔️ Roadside inspection created");
      } else {
        await axios.put(`${API_BASE}/api/roadside/${riId}`, payload, {
          headers,
        });
        alert("✔️ Roadside inspection updated");
      }

      // upload newly added attachments (local only have file)
      for (const a of form.attachments) {
        if (!a.file) continue;
        const fd = new FormData();
        fd.append("file", a.file);
        fd.append("ri_id", currentId);
        fd.append("ri_userid", userId);
        fd.append("ria_type", a.type);
        await axios.post(
          `${API_BASE}/api/roadside/attachments/upload?userId=${userId}`,
          fd,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      }

      // delete marked ones
      for (const delId of deletedAttachmentIds) {
        await axios.delete(`${API_BASE}/api/roadside/attachments/${delId}`, {
          headers,
        });
      }

      navigate(`/roadside/${userId}/detail/${currentId}`);
      window.location.reload();
    } catch (e) {
      console.error("❌ Save roadside inspection", e);
      alert("Save failed");
    }
  };

  // tiny helpers for arrays
  const updateArray = (key, idx, field, value) => {
    setForm((p) => {
      const arr = [...p[key]];
      arr[idx] = { ...arr[idx], [field]: value };
      return { ...p, [key]: arr };
    });
  };
  const removeArrayItem = (key, idx) =>
    setForm((p) => ({ ...p, [key]: p[key].filter((_, i) => i !== idx) }));

  // ---------- render
  return (
    <Container className="form-wrapper my-4">
      <div className="d-flex justify-content-between align-items-center pt-3">
        <span style={{ fontSize: "18px", fontWeight: "500" }}>
          DOT Inspection
        </span>
        <div className="d-flex gap-2">
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => navigate(`/roadside/${userId}`)}
          >
            View List
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate(-1)}
          >
            Return
          </button>
        </div>
      </div>

      <div className="form-wrapper my-4">
        <div className="row align-items-center mb-4">
          <div className="col-3 text-start">
            <img src={logo} alt="Company Logo" style={{ width: "150px" }} />
          </div>
          <div className="col-6 text-center">
            <h2 className="m-0">DOT Inspection</h2>
          </div>
          <CompanyAddress />
        </div>

        <Card>
          <Card.Body>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            {/* ========== Inspection Information ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header">
                <h5 className="m-0">
                  <i className="bi bi-journal-check me-2"></i>Inspection
                  Information
                </h5>
                <div className="inspection-toggle yesno-inline">
                  <label>DOT Inspection / Violation?</label>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn ${
                        form.ri_dot_flag ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_dot_flag: true }))
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        !form.ri_dot_flag
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_dot_flag: false }))
                      }
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>

              <div className="inspection-grid">
                <div className="grid-item span-2">
                  <label>
                    Report Number <span className="text-danger">*</span>
                  </label>
                  <input
                    className="form-control"
                    name="ri_number"
                    value={form.ri_number || ""}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="grid-item">
                  <label>Inspection Level</label>
                  <select
                    className="form-control"
                    name="ri_inspection_level"
                    value={form.ri_inspection_level || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Level</option>
                    {inspectionLevelOptions.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid-item yesno-group">
                  <label>Clean?</label>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn ${
                        form.ri_is_clean ? "btn-primary" : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_is_clean: true }))
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        !form.ri_is_clean
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_is_clean: false }))
                      }
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="grid-item">
                  <label>
                    Inspection Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className="form-control"
                    name="ri_inspection_date"
                    value={form.ri_inspection_date || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="time-group">
                  <div className="grid-item small">
                    <label>Start Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="ri_start_time"
                      value={form.ri_start_time || ""}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="grid-item small">
                    <label>End Time</label>
                    <input
                      type="time"
                      className="form-control"
                      name="ri_end_time"
                      value={form.ri_end_time || ""}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="grid-item hazmat-wide">
                  <label>Hazmat Inspection Level</label>
                  <select
                    className="form-control"
                    name="ri_hazmat_level"
                    value={form.ri_hazmat_level || ""}
                    onChange={handleChange}
                  >
                    <option value="">Select Level</option>
                    {hazmatInspectionLevelOptions.map((hopt) => (
                      <option key={hopt} value={hopt}>
                        {hopt}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid-item">
                  <label>Reported Time</label>
                  <input
                    type="time"
                    className="form-control"
                    name="ri_reported_time"
                    value={form.ri_reported_time || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid-item">
                  <label>Reported Date</label>
                  <input
                    type="date"
                    className="form-control"
                    name="ri_reported_date"
                    value={form.ri_reported_date || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="grid-item span-2 yesno-group">
                  <label>🚚 Was the driver in a commercial vehicle?</label>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn ${
                        form.ri_is_commercial
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_is_commercial: true }))
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        !form.ri_is_commercial
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({ ...p, ri_is_commercial: false }))
                      }
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== Location ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                <h5 className="m-0">
                  <i className="bi bi-geo-alt-fill me-2"></i>Location
                </h5>
              </div>

              <div className="location-grid">
                <input
                  className="form-control"
                  placeholder="Location"
                  value={form.location?.location || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, location: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="Milepost"
                  value={form.location?.milepost || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, milepost: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="Highway"
                  value={form.location?.highway || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, highway: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="State/Province"
                  value={form.location?.state || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, state: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="County"
                  value={form.location?.county || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, county: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="Cargo"
                  value={form.location?.cargo || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, cargo: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="Shipper"
                  value={form.location?.shipper || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, shipper: e.target.value },
                    }))
                  }
                />
                <input
                  className="form-control"
                  placeholder="Origin"
                  value={form.location?.origin || ""}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      location: { ...p.location, origin: e.target.value },
                    }))
                  }
                />
              </div>
            </div>

            {/* ========== Vehicle Identification ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                <h5 className="m-0">
                  <i className="bi bi-gear-fill me-2"></i>Vehicle Identification
                </h5>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      ri_vehicle_identification: [
                        ...p.ri_vehicle_identification,
                        {
                          unit: p.ri_vehicle_identification.length + 1,
                          number: "",
                          type: "",
                          license: "",
                          vin: "",
                          cvsa: "",
                          oos: "",
                        },
                      ],
                    }))
                  }
                >
                  + Add Row
                </button>
              </div>

              <div className="vehicle-identification-table">
                <div className="vehicle-header-row">
                  <div>Unit</div>
                  <div>Truck/Trailer # | Type</div>
                  <div>Licence # | VIN #</div>
                  <div>CVSA # | OOS #</div>
                  <div></div>
                </div>

                {form.ri_vehicle_identification.map((v, idx) => (
                  <div key={idx} className="vehicle-data-row">
                    <div className="vehicle-unit">{idx + 1}</div>
                    <div className="vehicle-input-group">
                      <input
                        className="form-control mb-2"
                        placeholder="Truck/Trailer #"
                        value={v.number}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "number",
                            e.target.value
                          )
                        }
                      />
                      <input
                        className="form-control"
                        placeholder="Type"
                        value={v.type}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "type",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="vehicle-input-group">
                      <input
                        className="form-control mb-2"
                        placeholder="Licence #"
                        value={v.license}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "license",
                            e.target.value
                          )
                        }
                      />
                      <input
                        className="form-control"
                        placeholder="VIN #"
                        value={v.vin}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "vin",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <div className="vehicle-input-group">
                      <input
                        className="form-control mb-2"
                        placeholder="CVSA #"
                        value={v.cvsa}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "cvsa",
                            e.target.value
                          )
                        }
                      />
                      <input
                        className="form-control"
                        placeholder="OOS #"
                        value={v.oos}
                        onChange={(e) =>
                          updateArray(
                            "ri_vehicle_identification",
                            idx,
                            "oos",
                            e.target.value
                          )
                        }
                      />
                    </div>

                    <td className="text-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-delete"
                        onClick={() =>
                          removeArrayItem("ri_vehicle_identification", idx)
                        }
                      >
                        🗑
                      </button>
                    </td>
                  </div>
                ))}
              </div>
            </div>

            {/* ========== Brake Adjustments ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                <h5 className="m-0">Brake Adjustments</h5>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() =>
                    setForm((p) => {
                      const currentCols = p.ri_brake_adjustments[0]
                        ? Object.keys(p.ri_brake_adjustments[0].values || {})
                            .length || 5
                        : 5;
                      const newAxle = currentCols + 1;
                      const updated = p.ri_brake_adjustments.map((row) => ({
                        ...row,
                        values: { ...(row.values || {}), [newAxle]: "" },
                      }));
                      return { ...p, ri_brake_adjustments: updated };
                    })
                  }
                >
                  + Add Row
                </button>
              </div>

              <div className="table-responsive brake-table-wrapper">
                <table className="table table-bordered roadside-table brake-table">
                  <thead>
                    <tr>
                      <th className="brake-axle-header">Axle #</th>
                      {[
                        ...Array(
                          Object.keys(
                            form.ri_brake_adjustments[0]?.values || {}
                          ).length || 5
                        ),
                      ].map((_, i) => (
                        <th key={i}>{i + 1}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {form.ri_brake_adjustments.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td className="fw-semibold">{row.label}</td>
                        {[
                          ...Array(
                            Object.keys(
                              form.ri_brake_adjustments[0]?.values || {}
                            ).length || 5
                          ),
                        ].map((_, colIndex) => (
                          <td key={colIndex}>
                            <input
                              type="text"
                              className="form-control"
                              value={row.values?.[colIndex + 1] || ""}
                              onChange={(e) => {
                                setForm((p) => {
                                  const arr = [...p.ri_brake_adjustments];
                                  arr[rowIndex].values = {
                                    ...(arr[rowIndex].values || {}),
                                    [colIndex + 1]: e.target.value,
                                  };
                                  return { ...p, ri_brake_adjustments: arr };
                                });
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ========== Violations ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                <h5 className="m-0">
                  <i className="bi bi-hammer me-2"></i>Violations
                </h5>
                <button
                  type="button"
                  className="btn btn-add btn-sm"
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      ri_violations: [
                        ...p.ri_violations,
                        {
                          sectionCode: "",
                          unit: "",
                          oos: "No",
                          citation: "",
                          verify: "No",
                          description: "",
                        },
                      ],
                    }))
                  }
                >
                  <i className="bi bi-plus-circle me-1"></i> Add
                </button>
              </div>

              <div className="violations-table">
                {/* Table Header */}
                <div className="violations-header">
                  <div>Section Code</div>
                  <div>Unit</div>
                  <div>OOS</div>
                  <div>Citation #</div>
                  <div>Verify</div>
                  <div>Description</div>
                  <div></div>
                </div>

                {/* Table Rows */}
                {form.ri_violations.map((v, i) => (
                  <div key={i} className="violations-row">
                    {/* Section Code */}
                    <select
                      className="form-select"
                      value={v.sectionCode}
                      onChange={(e) =>
                        updateArray(
                          "ri_violations",
                          i,
                          "sectionCode",
                          e.target.value
                        )
                      }
                    >
                      <option value="">Select Code</option>
                      <option value="392.2">392.2</option>
                      <option value="393.47">393.47</option>
                      <option value="396.3(a)(1)">396.3(a)(1)</option>
                    </select>

                    {/* Unit */}
                    <select
                      className="form-select short"
                      value={v.unit}
                      onChange={(e) =>
                        updateArray("ri_violations", i, "unit", e.target.value)
                      }
                    >
                      <option value="">Select</option>
                      <option value="D">D</option>
                      <option value="1">1</option>
                    </select>

                    {/* OOS */}
                    <select
                      className="form-select short"
                      value={v.oos}
                      onChange={(e) =>
                        updateArray("ri_violations", i, "oos", e.target.value)
                      }
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>

                    {/* Citation # */}
                    <input
                      type="text"
                      className="form-control short"
                      placeholder="Citation #"
                      value={v.citation}
                      style={{ textAlign: "left" }}
                      onChange={(e) =>
                        updateArray(
                          "ri_violations",
                          i,
                          "citation",
                          e.target.value
                        )
                      }
                    />

                    {/* Verify */}
                    <select
                      className="form-select short"
                      value={v.verify}
                      onChange={(e) =>
                        updateArray(
                          "ri_violations",
                          i,
                          "verify",
                          e.target.value
                        )
                      }
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>

                    {/* Description */}
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Description"
                      value={v.description}
                      style={{ textAlign: "left" }}
                      onChange={(e) =>
                        updateArray(
                          "ri_violations",
                          i,
                          "description",
                          e.target.value
                        )
                      }
                    />

                    {/* Delete Button */}
                    <button
                      type="button"
                      className="btn btn-delete btn-sm"
                      onClick={() => removeArrayItem("ri_violations", i)}
                    >
                      <i className="bi bi-trash"></i>
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* ========== Penalty / Fine ========== */}
            <div className="roadside-section card p-3 mb-3">
              <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                <h5 className="m-0">
                  <i className="bi bi-cash-coin me-2"></i>Penalty / Fine
                </h5>
              </div>

              {/* --- Flags Section --- */}
              <div className="penalty-flags mb-3">
                <div className="penalty-flags-header">Check all that apply</div>
                <div className="penalty-flags-body d-flex flex-wrap gap-4 p-3">
                  {[
                    { key: "suspension", label: "Suspension" },
                    { key: "revocation", label: "Revocation" },
                    { key: "communityService", label: "Community Service" },
                    { key: "other", label: "Other" },
                  ].map((f) => (
                    <label key={f.key} className="form-check penalty-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={form.ri_penalties.flags[f.key]}
                        onChange={(e) =>
                          setForm((p) => ({
                            ...p,
                            ri_penalties: {
                              ...p.ri_penalties,
                              flags: {
                                ...p.ri_penalties.flags,
                                [f.key]: e.target.checked,
                              },
                            },
                          }))
                        }
                      />
                      <span className="form-check-label ms-2">{f.label}</span>
                    </label>
                  ))}
                  {form.ri_penalties.flags.other && (
                    <input
                      className="form-control w-auto ms-3"
                      placeholder="Other description"
                      value={form.ri_penalties.otherText || ""}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          ri_penalties: {
                            ...p.ri_penalties,
                            otherText: e.target.value,
                          },
                        }))
                      }
                    />
                  )}
                </div>
              </div>

              {/* --- Citations Table --- */}
              <div className="penalty-citation-section">
                <div className="roadside-header d-flex justify-content-between align-items-center mb-2">
                  <h6 className="m-0">Citations</h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      setForm((p) => ({
                        ...p,
                        ri_penalties: {
                          ...p.ri_penalties,
                          citations: [
                            ...p.ri_penalties.citations,
                            { citationNumber: "", fine: 0, chargeBack: 0 },
                          ],
                        },
                      }))
                    }
                  >
                    + Add Row
                  </button>
                </div>

                {/* Header Row */}
                <div className="penalty-header-row mb-2">
                  <div>Citation #</div>
                  <div>Fine</div>
                  <div>Charge Back</div>
                  <div></div>
                </div>

                {form.ri_penalties.citations.map((c, idx) => (
                  <div key={idx} className="penalty-row">
                    <input
                      className="form-control"
                      placeholder="Citation Number"
                      value={c.citationNumber}
                      onChange={(e) => {
                        const arr = [...form.ri_penalties.citations];
                        arr[idx].citationNumber = e.target.value;
                        setForm((p) => ({
                          ...p,
                          ri_penalties: { ...p.ri_penalties, citations: arr },
                        }));
                      }}
                    />
                    <input
                      type="number"
                      className="form-control text-center"
                      value={c.fine}
                      onChange={(e) => {
                        const arr = [...form.ri_penalties.citations];
                        arr[idx].fine = e.target.value;
                        setForm((p) => ({
                          ...p,
                          ri_penalties: { ...p.ri_penalties, citations: arr },
                        }));
                      }}
                    />
                    <input
                      type="number"
                      className="form-control text-center"
                      value={c.chargeBack}
                      onChange={(e) => {
                        const arr = [...form.ri_penalties.citations];
                        arr[idx].chargeBack = e.target.value;
                        setForm((p) => ({
                          ...p,
                          ri_penalties: { ...p.ri_penalties, citations: arr },
                        }));
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-delete"
                      onClick={() => {
                        const arr = form.ri_penalties.citations.filter(
                          (_, i) => i !== idx
                        );
                        setForm((p) => ({
                          ...p,
                          ri_penalties: { ...p.ri_penalties, citations: arr },
                        }));
                      }}
                    >
                      🗑
                    </button>
                  </div>
                ))}
              </div>

              {/* --- Include Flags --- */}
              <div className="penalty-include mt-3 d-flex flex-wrap gap-4">
                <div className="yesno-inline">
                  <label className="fw-semibold mb-0">
                    Include on Annual Review?
                  </label>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn ${
                        form.ri_penalties.includeAnnualReview
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          ri_penalties: {
                            ...p.ri_penalties,
                            includeAnnualReview: true,
                          },
                        }))
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        !form.ri_penalties.includeAnnualReview
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          ri_penalties: {
                            ...p.ri_penalties,
                            includeAnnualReview: false,
                          },
                        }))
                      }
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="yesno-inline">
                  <label className="fw-semibold mb-0">
                    Include on Separation Report?
                  </label>
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className={`btn ${
                        form.ri_penalties.includeSeparationReport
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          ri_penalties: {
                            ...p.ri_penalties,
                            includeSeparationReport: true,
                          },
                        }))
                      }
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      className={`btn ${
                        !form.ri_penalties.includeSeparationReport
                          ? "btn-primary"
                          : "btn-outline-primary"
                      }`}
                      onClick={() =>
                        setForm((p) => ({
                          ...p,
                          ri_penalties: {
                            ...p.ri_penalties,
                            includeSeparationReport: false,
                          },
                        }))
                      }
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ========== Upload Attachments (Image 3 mid) ========== */}
            <div className="card p-3 mb-3">
              <h5>Upload Attachments</h5>
              {isNew ? (
                <div className="alert alert-info mb-0">
                  Please save the inspection before uploading attachments.
                </div>
              ) : (
                <>
                  <div className="row">
                    <div className="col-md-6">
                      <label>Attachment Type</label>
                      <select
                        className="form-control"
                        value={selectedAttachmentType}
                        onChange={(e) =>
                          setSelectedAttachmentType(e.target.value)
                        }
                      >
                        <option value="">Select an attachment type</option>
                        <option value="Acciedent Report">
                          Acciedent Report
                        </option>
                        <option value="Witness Statement">
                          Witness Statement
                        </option>
                        <option value="Photos">Photos</option>
                        <option value="Ticket">Ticket</option>
                        <option value="Insurance Claim">Insurance Claim</option>
                        <option value="Corrective Action">
                          Corrective Action
                        </option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label>Choose File</label>
                      <input
                        type="file"
                        className="form-control"
                        onChange={(e) => {
                          if (!e.target.files[0] || !selectedAttachmentType)
                            return;
                          const f = e.target.files[0];
                          setForm((p) => ({
                            ...p,
                            attachments: [
                              ...p.attachments,
                              {
                                id: null,
                                file: f,
                                type: selectedAttachmentType,
                                name: f.name,
                              },
                            ],
                          }));
                          setSelectedAttachmentType("");
                          e.target.value = "";
                        }}
                      />
                    </div>
                  </div>

                  {form.attachments.length > 0 && (
                    <UploadedAttachmentList
                      title="Uploaded Files"
                      files={form.attachments}
                      getFileKey={(file, index) => file.id || `pending-${index}`}
                      getFileName={(file) =>
                        file.id ? file.ria_original : file.name
                      }
                      getFileMeta={(file) => file.type}
                      getFileViewUrl={(file) =>
                        file.id
                          ? `${API_BASE}/uploads/roadsideattachments/${file.ria_file}`
                          : URL.createObjectURL(file.file)
                      }
                      getFileDownloadName={(file) =>
                        file.id ? file.ria_original : file.name
                      }
                      onDelete={(file, index) => {
                        if (file.id) {
                          setDeletedAttachmentIds((prev) => [...prev, file.id]);
                        }
                        setForm((previous) => ({
                          ...previous,
                          attachments: previous.attachments.filter(
                            (_, attachmentIndex) => attachmentIndex !== index,
                          ),
                        }));
                      }}
                    />
                  )}
                </>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <button type="submit" className="btn btn-primary mt-3">
                Save
              </button>
            </div>
          </form>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
};

export default RoadsideInspectionForm;


